/**
 * Sprint BG-DataSettings 30 — ModeProfile (modeTemplates の動的化版) CRUD hook。
 *
 * 旧 v1: `getModeTemplate(mode)` は static (`packages/shared-types/src/domain/modeTemplates.ts`)、
 * 新地区作成時に code 変更が必要だった。
 *
 * 新方式: Firestore `modeProfiles/{profileId}` を master 系と同 pattern で CRUD。
 *   - read: 認証済全員 (firestore.rules)
 *   - create/update: master role のみ
 *   - delete: 禁止 (archivedAt 論理削除)
 *
 * MasterCommon は継承しないが、versioning / archivedAt パターンは同じため、
 * useMasters 系の `upsertMaster` ではなく、本 hook 専用の薄い upsert を提供する
 * (profileVersion 文字列の自由度 + MasterCommon の `version: number` 不要のため)。
 */

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import {
  MODE_PROFILES_COLLECTION,
  parseModeProfile,
  type ModeProfile,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export type ModeProfilesState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: ModeProfile[]; error: null }
  | { status: 'error'; items: []; error: Error };

function mapProfileDoc(snap: QueryDocumentSnapshot<DocumentData>): ModeProfile | null {
  const data = snap.data();
  return parseModeProfile(data, snap.id);
}

/**
 * `/modeProfiles` を name 昇順で subscribe。
 * archivedAt 付きはデフォルトで除外 (`filterArchived: false` で表示も可)。
 */
export function useModeProfiles(options?: { filterArchived?: boolean }): ModeProfilesState {
  const filterArchived = options?.filterArchived !== false;
  const [state, setState] = useState<ModeProfilesState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    const ref = collection(db, MODE_PROFILES_COLLECTION);
    const q = query(ref, orderBy('name', 'asc'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: ModeProfile[] = [];
        for (const d of snap.docs) {
          const parsed = mapProfileDoc(d);
          if (parsed) items.push(parsed);
        }
        const filtered = filterArchived ? items.filter((p) => !p.archivedAt) : items;
        setState({ status: 'ready', items: filtered, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsub;
  }, [filterArchived]);

  return state;
}

export interface UpsertModeProfileInput {
  /** kebab-case id (新規時必須、既存は固定)。 */
  id: string;
  /** caller uid (createdBy / updatedBy に記録)。 */
  uid: string;
  /** 新規作成か (true) or 既存更新か (false)。 */
  isNew: boolean;
  /** 書込む payload (id / createdAt / updatedAt / createdBy / updatedBy は自動付与)。 */
  data: Omit<
    ModeProfile,
    'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt'
  >;
}

/**
 * ModeProfile 1 件を upsert (新規 create or 既存 update)。
 *
 * - 新規時: `archivedAt: null` を初期化、`createdAt` / `createdBy` を設定
 * - 更新時: `archivedAt` は上書きせず温存 (archive は別 callable で行う)
 */
export async function upsertModeProfile(input: UpsertModeProfileInput): Promise<string> {
  const ref = doc(db, MODE_PROFILES_COLLECTION, input.id);
  const now = serverTimestamp();
  const payload: Record<string, unknown> = {
    ...input.data,
    id: input.id,
    updatedAt: now,
    updatedBy: input.uid,
  };
  if (input.isNew) {
    payload.createdAt = now;
    payload.createdBy = input.uid;
    payload.archivedAt = null;
    // reviewer follow-up ⚠️-1: 他マスタ (useMasters.upsertMaster) と同じく DocCommon 規約に従い
    // 新規 create 時のみ schemaVersion=1 を自動付与。input.data に明示値があればそれを尊重 (spread 後 default)。
    if (!('schemaVersion' in input.data)) payload.schemaVersion = 1;
  } else {
    // 既存 update 時は schemaVersion を上書きしない (migration script の責務、useMasters と同方針)。
    delete payload.schemaVersion;
  }
  await setDoc(ref, payload, { merge: !input.isNew });
  return input.id;
}

/**
 * ModeProfile を archivedAt = serverTimestamp() で論理削除。
 * 物理削除は rules で禁止。
 */
export async function archiveModeProfile(id: string, uid: string): Promise<void> {
  const ref = doc(db, MODE_PROFILES_COLLECTION, id);
  await setDoc(
    ref,
    {
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}
